import React from "react";
import Rating, { RatingProps } from "@mui/material/Rating";
import { EmtIcon } from "./EmtIcon";
import { EmtTypography } from "./EmtTypography";
import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";

export type EmtRatingProps = RatingProps & {
  /**
   * Icon variant
   * @default "star"
   */
  iconVariant?: "star" | "heart";
  /**
   * Set the empty icon color based en MUI theme naming
   * @default "greyGradient.300"
   */
  emptyIconColor?: string;
  /**
   * Set if inactive icons are filled, don't work if you customise "emptyIcon" props
   * @default true
   */
  filled?: boolean;
  /**
   * Set the inactive icon size
   * @default undefined
   */
  emptyIconSize?: "small" | "medium" | "large";
  /**
   * label for rating
   * @default undefined
   */
  label?: React.ReactNode;
  /**
   * label position
   * @default "right"
   */
  labelFloat?: "left" | "right";
  /**
   * Font size for the label, use the MUI theme naming
   * @default "body2"
   */
  labelFontSize?:
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "caption"
    | "body1"
    | "body2";
  labelFontColor?: string;
  /**
   * The sx props for the box arround <Rating>
   * @default {}
   */
  boxSx?: SxProps<Theme>;
  nonce?: string;
};

export function EmtRating(props: EmtRatingProps) {
  const {
    icon,
    emptyIcon,
    size = "medium",
    emptyIconSize,
    iconVariant = "star",
    filled = true,
    color = "#FFDE54",
    emptyIconColor = "greyGradient.100",
    sx,
    label,
    labelFloat = "right",
    labelFontSize = "body2",
    labelFontColor = "greyGradient.main",
    boxSx = {},
    ...rest
  } = props;

  const labelComponent = (
    <EmtTypography
      variant={labelFontSize}
      color={labelFontColor}
      sx={{
        ...(labelFloat == "left" ? { mr: 0.5 } : { ml: 0 }),
        fontWeight: "bold",
        display: "inline-block",
        lineHeight: 0,
      }}
    >
      <>{label}</>
    </EmtTypography>
  );

  return (
    <Box sx={boxSx}>
      {label && labelFloat === "left" ? labelComponent : null}
      <Rating
        {...rest}
        icon={
          icon ? (
            icon
          ) : (
            <EmtIcon
              className={`emt-${iconVariant}`}
              fontSize={size}
              sx={{ color }}
            />
          )
        }
        sx={{
          ...sx,
          alignItems: "center",
        }}
        emptyIcon={
          emptyIcon ? (
            emptyIcon
          ) : (
            <EmtIcon
              className={`emt-${iconVariant}${!filled ? "-1" : ""}`}
              fontSize={emptyIconSize ? emptyIconSize : size}
              sx={{
                color: rest.max === 1 && label ? "#FFDE54" : emptyIconColor,
              }}
            />
          )
        }
      />
      {label && labelFloat === "right" ? labelComponent : null}
    </Box>
  );
}
